import React, {
  useMemo,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withFocus } from '@accedo/vdkweb-navigation';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
  loadContainerShows,
  clearContainerShows,
} from '#/redux/modules/firestore/actions';
import {
  getContainerShows,
  getContainerPodcasts,
} from '#/redux/modules/firestore/selectors';
import { getEntryById } from '#/redux/modules/accedoOne/selectors';
import getVw from '#/utils/getVw';
import imgixUrl from '#/utils/imgix';
import { subRoutes } from '#/utils/routes';
import {
  containersTypeAlias,
  imgixTileResizeParameters,
  SWIMLANE_LENGTH,
  SWIMLANE_RIGHT_ARROW,
  SWIMLANE_LEFT_ARROW,
  mainPageNames,
} from '#/config/constants';

import ShowCard from '#/components/ShowCard/ShowCard';
import showCardStyles from '#/components/ShowCard/showcard.scss';
import Swimlane from '#/components/Swimlane/Swimlane';
import styles from './fsCollectionSwimlaneFactory.scss';
import { isWorkstation } from '#/utils/componentStyleConfig';
import { setSpeech } from '#/utils/accessibility';
import { isItemOutsideScreen } from '#/utils/isItemOutsideScreen';
import { FocusDiv } from '#/widgets/ui';

import { getComponentItems } from '../swimlaneFactoryUtils';

const SeeAllCard = ({
  nav,
  onClick,
  className,
  style,
  children,
  ariaLabel,
}) => {
  return (
    <div
      tabIndex={!isWorkstation() ? -1 : 0}
      id={nav.id}
      onClick={onClick}
      className={classNames(className, { [styles.isFocused]: nav.isFocused })}
      style={style}
      aria-label={ariaLabel}
    >
      <div aria-hidden="true">{children}</div>
    </div>
  );
};
const FocusSeeAllCard = withFocus(SeeAllCard);
SeeAllCard.propTypes = {
  nav: PropTypes.object,
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.any,
  style: PropTypes.any,
  ariaLabel: PropTypes.string,
};

const FSCollectionSwimlaneFactory = ({
  collectionItems,
  collectionName,
  containerId,
  title,
  nav,
  ageTheme,
  history,
  enableSeeAll = true,
  alwaysVisible = false,
  onFocus,
  location,
  useLastFocus = true,
}) => {
  const { separatorColor, backgroundColor } =
    useSelector(state => getEntryById(state, containerId)) || {};
  const dispatch = useDispatch();
  const getItemId = useCallback(index => `${nav.id}-ITEM-${index}`, []);
  const SEE_ALL = `${nav.id}-see-all`;
  const currentIndex = useRef(0);
  const previousIndex = useRef(0);
  const currentPath = location?.pathname?.split('/').slice(-1)[0];
  const containerItem = () => {
    if (collectionName?.includes('podcast')) {
      return useSelector(getContainerPodcasts);
    }

    return (
      useSelector(getContainerShows)[collectionName] || {
        collection: null,
        items: [],
      }
    );
  };

  const { collection = '', items } = collectionItems
    ? { items: collectionItems }
    : containerItem();
  const [rowChanged, setRowChanged] = useState(true);
  const itemFocusChanged = useRef(() => {});
  const setFocusChanged = useCallback(func => {
    itemFocusChanged.current = func;
  }, []);

  useEffect(() => {
    if (
      !items.length &&
      !collectionItems &&
      collectionName &&
      !collectionName?.includes('podcast')
    ) {
      dispatch(loadContainerShows(collectionName));
    }

    // return () => {
    //   dispatch(clearContainerShows());
    // };
  }, [items, collection, collectionItems, collectionName]);

  // load podcast for selected age after age change
  useEffect(() => {
    dispatch(clearContainerShows());

    if (
      collectionName?.includes('podcast') &&
      !collectionItems &&
      collectionName
    ) {
      dispatch(loadContainerShows(collectionName));
    }
  }, [ageTheme?.ageGroupValue]);

  const handleNavigation = useCallback((nextIndex, itemsLength) => {
    if (nextIndex < 0 || nextIndex > itemsLength - 1) {
      return;
    }
    setRowChanged(false);
    currentIndex.current = nextIndex;

    return false;
  }, []);

  const onItemFocus = useCallback(
    id => {
      if (onFocus) {
        onFocus(nav.id);
      }

      // If no id then assume we're jumping to the first tile
      id = id ?? `${nav.id}-VIDEO-0`;

      previousIndex.current = currentIndex.current;
      currentIndex.current = id;

      itemFocusChanged.current(id);
    },
    [onFocus, nav.id, currentIndex],
  );

  useEffect(() => {
    previousIndex.current = 0;
    currentIndex.current = 0;

    if (!useLastFocus) {
      itemFocusChanged.current(getItemId(currentIndex.current));
    }
  }, [items]);

  const getShowPlaylistId = useCallback(show => show?.playlist_id || '0', []);

  const generateItemNavs = useCallback(
    i => {
      const itemsLimit = items.limit || SWIMLANE_LENGTH;
      return {
        parent: nav.id,
        id: getItemId(i),
        ...(i !== 0 ? { nextleft: getItemId(i - 1) } : {}),
        ...(i + 1 === itemsLimit && enableSeeAll
          ? { nextright: SEE_ALL }
          : i !== items.length - 1
          ? { nextright: getItemId(i + 1) }
          : {}),
        internal: {
          nextright: () => handleNavigation(i + 1, items.length),
          nextleft: () => handleNavigation(i - 1, items.length),
          nextup: () => setRowChanged(true),
          nextdown: () => setRowChanged(true),
        },
      };
    },
    [items],
  );

  useEffect(() => {
    const itemsLimit = items.limit || SWIMLANE_LENGTH;
    const currentItemId =
      currentIndex.current === itemsLimit && enableSeeAll
        ? SEE_ALL
        : getItemId(currentIndex.current);
    if (!isWorkstation() && isItemOutsideScreen(currentItemId)) {
      if (previousIndex.current < currentIndex.current) {
        document.getElementById(SWIMLANE_RIGHT_ARROW + nav.id)?.click();
      } else if (previousIndex.current > currentIndex.current) {
        document.getElementById(SWIMLANE_LEFT_ARROW + nav.id)?.click();
      }
    }
    previousIndex.current = currentIndex.current;
  }, [currentIndex, previousIndex, items]);

  const createAriaLabel = useCallback(
    item => {
      const label = `${item.name}`;
      if (rowChanged) {
        return `${title || 'Shows'}, ${label}`;
      }
      return label;
    },
    [rowChanged],
  );

  const [showCards, emptyItems] = useMemo(() => {
    const itemsLimit = items.limit || SWIMLANE_LENGTH;
    const [cards, emptyCards] = getComponentItems(
      items.slice(0, enableSeeAll ? itemsLimit : items.length),
      (item, index) => {
        const commonProps = {
          nav: generateItemNavs(index),
          key: getItemId(index),
          ariaLabel: createAriaLabel(item),
        };

        return [
          <ShowCard
            {...commonProps}
            className={classNames(styles.cardContainer, currentPath || '')}
            id={item.id}
            name={item.name}
            imgUrl={item.key_art_horizontal_image_path}
            roundedCorners={ageTheme?.tileRounded}
            onItemFocus={onItemFocus}
            playlistId={getShowPlaylistId(item)}
          />,
          <FocusDiv
            {...commonProps}
            onFocus={onItemFocus}
            classNameFocused={showCardStyles.focused}
            className={classNames(
              showCardStyles.card,
              showCardStyles.empty,
              styles.cardContainer,
              currentPath || '',
              {
                [showCardStyles.isTv]: !isWorkstation(),
              },
            )}
          />,
        ];
      },
    );

    if (items.length > itemsLimit && enableSeeAll) {
      const path = history.location?.pathname?.split('/');
      const containerAlias =
        currentPath === mainPageNames.FAVORITES && !isWorkstation()
          ? containersTypeAlias.TVCONTAINER_FAV_SHOWS
          : containersTypeAlias.CONTAINER_SHOWS;

      emptyCards.push(
        <FocusDiv
          nav={{
            id: SEE_ALL,
            nextleft: getItemId(itemsLimit - 1),
            parent: nav.id,
          }}
          key={SEE_ALL}
          onFocus={() => {
            setSpeech(`See all, ${title}`);
            onItemFocus();
          }}
          ariaLabel="See All"
          classNameFocused={showCardStyles.focused}
          className={classNames(
            showCardStyles.card,
            showCardStyles.empty,
            styles.cardContainer,
            currentPath || '',
            {
              [showCardStyles.isTv]: !isWorkstation(),
            },
          )}
        />,
      );

      cards.push(
        <FocusSeeAllCard
          nav={{
            id: SEE_ALL,
            nextleft: getItemId(itemsLimit - 1),
            parent: nav.id,
          }}
          key={SEE_ALL}
          onClick={() =>
            history.push(
              `/${path[1]}/${path[2]}/${subRoutes.SEE_ALL}/${items[itemsLimit].id}/${title}/${containerAlias}`,
            )
          }
          style={{
            borderRadius: ageTheme?.tileRounded ? getVw(24) : '0px',
            outline: 'none',
          }}
          className={classNames(styles.seeAllShows, {
            [styles.isTv]: !isWorkstation(),
          })}
          onFocus={() => {
            setSpeech(`See all, ${title}`);
            onItemFocus();
          }}
          ariaLabel="See All"
        >
          <div
            aria-hidden="true"
            id={SEE_ALL}
            className={styles.seeAllImage}
            style={{
              backgroundImage: `url(${imgixUrl(
                items[itemsLimit].key_art_horizontal_image_path,
                imgixTileResizeParameters.SHOWS,
              )})`,
            }}
          >
            <div />
          </div>
          <div className={styles.seeAllText} aria-hidden="true">
            <p>See all</p>
          </div>
        </FocusSeeAllCard>,
      );
    }

    return [cards, emptyCards];
  }, [items, rowChanged, onItemFocus]);

  const collectionSwimlaneNav = {
    ...nav,
    forwardFocus: `${nav.id}-ITEM-0`,
    useLastFocus,
  };
  return showCards.length ? (
    <Swimlane
      isTv={!isWorkstation()}
      items={showCards}
      emptyItems={emptyItems}
      title={title}
      nav={collectionSwimlaneNav}
      navId={nav.id}
      alwaysVisible={alwaysVisible}
      setFocusChanged={setFocusChanged}
      separatorColor={separatorColor}
      backgroundColor={backgroundColor}
    />
  ) : null;
};
FSCollectionSwimlaneFactory.propTypes = {
  collectionItems: PropTypes.array,
  collectionName: PropTypes.string,
  containerId: PropTypes.string,
  title: PropTypes.string,
  nav: PropTypes.object,
  ageTheme: PropTypes.object,
  history: PropTypes.object,
  enableSeeAll: PropTypes.bool,
  alwaysVisible: PropTypes.bool,
  onFocus: PropTypes.any,
  location: PropTypes.object,
  useLastFocus: PropTypes.bool,
};

export default withFocus(withRouter(FSCollectionSwimlaneFactory));
